/**
 * Tarjeta visual para la gestión y previsualización de archivos adjuntos.
 * * Renderiza una vista previa (imagen o icono según el tipo MIME), permite la descarga
 * y ofrece una acción de selección/vista rápida. Incluye estilos locales para efectos de hover.
 * * @component
 * @param {Object} props - Propiedades del componente.
 * @param {Object} props.file - Objeto con la información del archivo.
 * @param {string} props.file.url - URL pública o endpoint para acceder al recurso.
 * @param {string} props.file.name - Nombre del archivo con su extensión.
 * @param {string} [props.file.mimeType] - Tipo de medio (ej: 'image/png', 'application/pdf').
 * @param {Function} props.onSelect - Función callback que se ejecuta al hacer clic en el botón de "ver".
 * Recibe el objeto `file` completo como argumento.
 * * @returns {JSX.Element} Una tarjeta interactiva con soporte para imágenes, PDFs, Excel y Word.
 */
const FileCard = ({ file, onSelect }) => {
  const isImage = file.mimeType?.startsWith('image/')

  /**
   * Determina la clase del icono de FontAwesome y el color de Bootstrap según el MIME.
   * @returns {string} Clases CSS para el icono.
   */
  const getIcon = () => {
    if (file.mimeType?.includes('pdf')) return 'fa-file-pdf text-danger'
    if (file.mimeType?.includes('excel')) return 'fa-file-excel text-success'
    if (file.mimeType?.includes('word')) return 'fa-file-word text-primary'
    return 'fa-file text-secondary'
  }

  /**
   * Genera una etiqueta legible para el badge de tipo de archivo.
   * @returns {string} Etiqueta descriptiva.
   */
  const getFileTypeLabel = () => {
    if (!file.mimeType) return 'Archivo'
    if (file.mimeType.startsWith('image/')) return 'Imagen'
    if (file.mimeType.includes('pdf')) return 'PDF'
    if (file.mimeType.includes('excel')) return 'Excel'
    if (file.mimeType.includes('word')) return 'Word'
    return 'Archivo'
  }

  return (
    <div className="file-card">
      <div className="card border-0 shadow-sm overflow-hidden h-100">
        {/* PREVIEW */}
        <div className="file-preview position-relative">
          {isImage ? (
            <img
              src={file.url}
              alt={file.name}
              className="w-100 h-100"
              style={{ objectFit: 'cover' }}
            />
          ) : (
            <div className="d-flex align-items-center justify-content-center h-100">
              <i className={`fas ${getIcon()} fa-2x`}></i>
            </div>
          )}

          {/* OVERLAY */}
          <div className="file-overlay d-flex align-items-center justify-content-center gap-2">
            <button
              onClick={() => onSelect(file)}
              className="btn btn-light btn-sm rounded-circle"
              type="button"
            >
              <i className="fas fa-eye"></i>
            </button>

            <a
              href={file.url}
              download
              className="btn btn-light btn-sm rounded-circle"
            >
              <i className="fas fa-download"></i>
            </a>
          </div>
        </div>

        {/* INFO */}
        <div className="p-2">
          <div className="fw-semibold small text-truncate" title={file.name}>
            {file.name}
          </div>

          <div className="d-flex justify-content-between align-items-center mt-1">
            <span className="badge bg-light text-dark border">
              {getFileTypeLabel()}
            </span>

            <i className="fas fa-paperclip text-muted small"></i>
          </div>
        </div>
      </div>

      {/* STYLES */}
      <style>
        {`
          .file-card {
            width: 150px;
            flex: 0 0 auto;
          }

          .file-preview {
            height: 95px;
            background: var(--bg-surface-2);
            position: relative;
          }

          .file-overlay {
            position: absolute;
            inset: 0;
            background: rgba(0,0,0,0.45);
            opacity: 0;
            transition: opacity .2s ease;
          }

          .file-card:hover .file-overlay {
            opacity: 1;
          }

          .file-card:hover .card {
            transform: translateY(-3px);
            box-shadow: 0 8px 20px rgba(0,0,0,0.12);
          }
        `}
      </style>
    </div>
  )
}

export default FileCard
